import { Controller } from "@hotwired/stimulus";

// Animates numbers inside h3 elements from 0 to their target value
// when the element scrolls into view. Supports commas, $, +, and % formatting.
// Uses IntersectionObserver to trigger once on visibility.
export default class extends Controller {
  static values = {
    duration: { type: Number, default: 2000 },
  };

  connect() {
    this.hasAnimated = false;
    this.stat = this.parseStat();
    if (!this.stat) return;

    this.stat.element.textContent = this.formatNumber(0, this.stat);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !this.hasAnimated) {
          this.hasAnimated = true;
          this.observer.disconnect();
          this.animate();
        }
      },
      { threshold: 0.2 },
    );
    this.observer.observe(this.element);
  }

  disconnect() {
    this.observer?.disconnect();
  }

  parseStat() {
    const el = this.element.querySelector("h3");
    if (!el) return null;

    const text = el.textContent.trim();
    const match = text.match(/^(\$?)([\d,]+)(\+|%?)$/);
    if (!match) return null;

    let textNode = el;
    while (textNode.firstElementChild) {
      textNode = textNode.firstElementChild;
    }

    return {
      element: textNode,
      targetNumber: parseInt(match[2].replace(/,/g, ""), 10),
      prefix: match[1],
      suffix: match[3],
      hasCommas: match[2].includes(","),
    };
  }

  animate() {
    const startTime = performance.now();
    const duration = this.durationValue;
    const stat = this.stat;

    const tick = (now) => {
      const elapsed = now - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);
      const progress = this.easeOutQuad(rawProgress);
      const current = Math.round(progress * stat.targetNumber);
      stat.element.textContent = this.formatNumber(current, stat);

      if (rawProgress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }

  easeOutQuad(t) {
    return t * (2 - t);
  }

  formatNumber(value, stat) {
    const formatted = stat.hasCommas
      ? value.toLocaleString("en-US")
      : value.toString();
    return `${stat.prefix}${formatted}${stat.suffix}`;
  }
}
